/**
 * Form state for a source, and the translation to the API's write payload.
 *
 * Separate from the component so the module exports only functions and types —
 * the page and the detail view both need `toPayload`, and mixing helpers into a
 * component module breaks fast refresh.
 */
import type { ConnectionDetail, ConnectionWrite } from "@/api/queries";

export type OptionValue = string | number | boolean;
export type SourceTypeKey = ConnectionWrite["source_type"];

export interface SourceFormValue {
  name: string;
  source_type: SourceTypeKey;
  description: string;
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  file_path: string;
  options: Record<string, OptionValue>;
}

export function emptyValue(sourceType: SourceTypeKey): SourceFormValue {
  return {
    name: "",
    source_type: sourceType,
    description: "",
    host: "",
    port: "",
    database: "",
    username: "",
    password: "",
    file_path: "",
    options: {},
  };
}

export function fromConnection(connection: ConnectionDetail): SourceFormValue {
  return {
    name: connection.name,
    source_type: connection.source_type as SourceTypeKey,
    description: connection.description,
    host: connection.host ?? "",
    port: connection.port ? String(connection.port) : "",
    database: connection.database ?? "",
    username: connection.username ?? "",
    // Never populated from the server: the API does not return it, by design.
    password: "",
    file_path: connection.file_path ?? "",
    options: (connection.options ?? {}) as Record<string, OptionValue>,
  };
}

/** Build the write payload, omitting the fields the API must not receive. */
export function toPayload(value: SourceFormValue, clearPassword: boolean): ConnectionWrite {
  const payload: ConnectionWrite = {
    name: value.name.trim(),
    source_type: value.source_type,
    description: value.description.trim(),
    options: value.options,
  };
  if (value.host.trim()) payload.host = value.host.trim();
  if (value.port.trim()) payload.port = Number(value.port);
  if (value.database.trim()) payload.database = value.database.trim();
  if (value.username.trim()) payload.username = value.username.trim();
  if (value.file_path.trim()) payload.file_path = value.file_path.trim();
  // An untouched password field means "keep the stored credential", never
  // "clear it" — only an explicit clear sends an empty string.
  if (value.password || clearPassword) payload.password = value.password;
  return payload;
}
